/**
 * VideoPreviewPlayer
 *
 * Plays the user's locally-selected video file via native HTML5 <video> + blob URL.
 * Custom UI: always-visible play/pause button + bottom progress bar. No native controls.
 * Falls back to a thumbnail when the MIME type is not natively playable (.avi, .mkv)
 * or when the browser fails to decode the file.
 */

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { ContentThumbnail } from '@/presentation/feature/content/components/ContentThumbnail';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import { MediaType } from '@/domain/enums/mediaType';
import { PlayPauseAnimatedButton } from '@/presentation/components/video/PlayPauseAnimatedButton';
import { LocalVideoProgressBar } from '@/presentation/components/video/LocalVideoProgressBar';
import { qaAttr } from '@/presentation/utils/qaAttr';

interface VideoPreviewPlayerProps {
  file: File;
  thumbnailUrl: string;
  duration: number;
  /**
   * Resolved media type (chat flow only). When this is {@link MediaType.IMAGE}
   * the preview renders the image directly with no video chrome and no
   * "unsupported preview" overlay. The user content flow is video-only and
   * never passes this, so the video branches below are unchanged.
   */
  mediaType?: MediaType | null;
}

export interface VideoPreviewPlayerRef {
  pause: () => void;
}

export const VideoPreviewPlayer = forwardRef<VideoPreviewPlayerRef, VideoPreviewPlayerProps>(({
  file,
  thumbnailUrl,
  duration,
  mediaType,
}, ref) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  const canPlayNatively = MediaUtils.isNativelyPlayableVideo(file);
  const [decodeFailed, setDecodeFailed] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useImperativeHandle(ref, () => ({
    pause: () => {
      const video = videoRef.current;
      if (video && !video.paused) {
        video.pause();
      }
    },
  }));

  const blobUrl = useMemo(
    () => (canPlayNatively ? URL.createObjectURL(file) : null),
    [file, canPlayNatively]
  );

  useEffect(() => {
    if (!blobUrl) return;
    return () => {
      URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  // Autoplay when video is first loaded
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !blobUrl) return;

    const handleCanPlay = () => {
      void video.play();
      video.removeEventListener('canplay', handleCanPlay);
    };
    video.addEventListener('canplay', handleCanPlay);
    return () => video.removeEventListener('canplay', handleCanPlay);
  }, [blobUrl]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  }, []);

  // Chat image sends: render the image directly. `thumbnailUrl` is the image's
  // own blob URL (useAddContent.ts), so there is no video chrome and no
  // "unsupported preview" overlay — the image is fully previewable.
  if (mediaType === MediaType.IMAGE) {
    return <ContentThumbnail thumbnailUrl={thumbnailUrl} alt="Image preview" />;
  }

  if (!canPlayNatively || decodeFailed) {
    return (
      <>
        <ContentThumbnail thumbnailUrl={thumbnailUrl} alt="Video preview" />
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            padding: responsive.spacing.sm,
            backgroundColor: theme.modalOverlay,
            color: theme.white,
            fontSize: responsive.font.xs,
            textAlign: 'center',
          }}
        >
          {l10n.publish.unsupportedPreview}
        </div>
      </>
    );
  }

  return (
    <>
      <video
        ref={videoRef}
        src={blobUrl ?? undefined}
        muted
        playsInline
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onError={() => setDecodeFailed(true)}
        onClick={togglePlay}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          backgroundColor: theme.black,
          cursor: 'pointer',
        }}
      />

      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <div
          {...qaAttr({
            id: 'add-content-preview-playpause',
            status: isPlaying ? 'playing' : 'paused',
          })}
          style={{ pointerEvents: 'auto' }}
        >
          <PlayPauseAnimatedButton
            isPlaying={isPlaying}
            onClick={togglePlay}
          />
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          left: responsive.spacing.base,
          right: responsive.spacing.base,
          bottom: 0,
        }}
      >
        <LocalVideoProgressBar videoRef={videoRef} duration={duration} />
      </div>
    </>
  );
});

VideoPreviewPlayer.displayName = 'VideoPreviewPlayer';
